import Button from '@srcDS/js/components/atoms/button/Button';
import { TextField } from '@srcDS/js/components/atoms/textField/TextField';
import { validateNewLetterSchema } from '@srcDS/js/components/molecules/forms/utils/validationSchemas';
import ModalSection from '@srcDS/js/components/molecules/modal/ModalSection';
import Modal from '@srcDS/js/components/molecules/modal/index';
import { Heading } from '@srcDS/js/components/typography/heading/Heading';
import { Text } from '@srcDS/js/components/typography/text/Text';
import { Title } from '@srcDS/js/components/typography/title/Title';
import styles from './NewsLetterPopup.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import PrimaryLoader from 'apgdev/src/js/components/atoms/primaryLoader/PrimaryLoader';
import addKlaviyoSubscriber from 'apgdev/src/js/utils/addKlaviyoSubscriber';
import GetCSRFToken from 'apgdev/src/js/components/atoms/getCSRFToken/GetCSRFToken';
import InlineMessage from 'apgdev/src/js/components/atoms/inlineMessage/InlineMessage';
import { useOpenSignForm } from '@apgnext/frontend/legacy/js/utils/useOpenSignForm';
import { FORM_TYPE } from '@apgnext/frontend/legacy/js/components/molecules/account/accountDropdown/AccountDropdown';

const NewsLetterPopup = ({
  showModal,
  onSet,
  startModalWidth,
  isMobilePaddings,
  label,
  title,
  text,
  typograghyPosition,
  image,
  isNewsletterClosed,
  customClasses,
  onCloseModal,
  ...rest
}) => {
  const locale = useSelector((state) => state.app.context.locale);
  const renderContext = useSelector((state) => state.renderContext || {});
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signFormOpen } = useOpenSignForm();
  const hasImage = Boolean(image?.media?.file);
  const {
    register,
    handleSubmit,
    reset,

    formState: {
      errors,
    },
  } = useForm({
    mode: 'all',
    validationSchema: validateNewLetterSchema,
  });

  const onSubmit = async ({ email, firstName, lastName }) => {
    sessionStorage.setItem('newsLetterEmail', email);

    const csrf_token = await GetCSRFToken('klaviyo_subscriber_add');
    const data = {
      email,
      csrf_token,
      firstName,
      lastName,
    };

    try {
      setError(null);
      setIsLoading(true);
      const res = await addKlaviyoSubscriber(data);
      if (res?.success) {
        setIsLoading(false);
        reset();
        onCloseModal();
        signFormOpen(FORM_TYPE.REGISTER);
        return;
      }
      setError(res.message);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    showModal &&
    !renderContext.serverSideRendering && (
      <Modal
        size="normal"
        startModalWidth={hasImage ? startModalWidth : '535'}
        isMobilePaddings={isMobilePaddings}
        hasCloseButton={true}
        sizeCloseButton="small"
        variantCloseButton="invisible"
        typeCloseButton="pill"
        shapeCloseButton="rounded"
        fillCloseButton="fill"
        widthCloseButton="30"
        heightCloseButton="30"
        isMobileFullPage={true}
        buttonBlockTop={15}
        buttonBlockRight={10}
        buttonBlockWidth={30}
        buttonBlockHeight={30}
        mobileButtonBlockTop={10}
        mobileButtonBlockRight={10}
        mobileButtonBlockWidth={26.5}
        mobileButtonBlockHeight={26.5}
        onClose={() => {
          onCloseModal(true);
        }}
        wrapperStyles={{ overflow: 'hidden' }}
        {...rest}
      >
        <ModalSection>
          <div className={styles.root}>
            {hasImage && (
              <div className={styles.imageBlock}>
                <div className={styles.imageWrapper}>
                  <img src={image.media.file} alt={image.title[locale]} />
                </div>
              </div>
            )}
            <div
              className={clsx(
                styles.content,
                !hasImage && styles.withoutImageContent,
              )}
            >
              <div className={styles.box}>
                <Title
                  customClasses={{
                    title: !hasImage ? styles.titleBoxNoImage : styles.titleBox,
                  }}
                >
                  <Text
                    className={clsx(
                      styles.subtitle,
                      !image && styles.subtitleWithoutImage,
                    )}
                    as="span"
                    size="xs"
                    weight="semibold"
                    textTransform="uppercase"
                  >
                    {label}
                  </Text>
                  <Heading
                    className={clsx(
                      styles.title,
                      !image && styles.titleWithoutImage,
                    )}
                    as="h2"
                    placement="common"
                    align={typograghyPosition}
                    textTransform="uppercase"
                  >
                    {title}
                  </Heading>
                  <Text
                    className={clsx(
                      styles.text,
                      !image && styles.textWithoutImage,
                    )}
                    as="p"
                    size="xs"
                    align={typograghyPosition}
                  >
                    {text}
                  </Text>
                </Title>
                <div
                  className={clsx(
                    styles.inputWrapper,
                    !image && styles.inputWrapperWithoutImage,
                  )}
                >
                  <form
                    className={styles.newsletterForm}
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <div className={styles.formRow}>
                      <TextField
                        customClasses={{
                          input: !image
                            ? styles.emailInputWithoutImage
                            : styles.newsletterInput,
                        }}
                        className={clsx(
                          styles.emailInputField,
                          !image
                            ? styles.emailInputFieldWithoutImage
                            : styles.emailInputField,
                        )}
                        type="text"
                        placeholder="First Name"
                        variant="underlined"
                        {...register('firstName')}
                        errorMessage={errors?.firstName?.message}
                        error={!!errors?.firstName}
                      />
                      <TextField
                        customClasses={{
                          input: !image
                            ? styles.emailInputWithoutImage
                            : styles.newsletterInput,
                        }}
                        className={clsx(
                          styles.emailInputField,
                          !image
                            ? styles.emailInputFieldWithoutImage
                            : styles.emailInputField,
                        )}
                        type="text"
                        placeholder="Last Name"
                        variant="underlined"
                        {...register('lastName')}
                        errorMessage={errors?.lastName?.message}
                        error={!!errors?.lastName}
                      />
                    </div>
                    <TextField
                      customClasses={{
                        input: !image
                          ? styles.emailInputWithoutImage
                          : styles.newsletterInput,
                      }}
                      className={clsx(
                        styles.emailInputField,
                        !image
                          ? styles.emailInputFieldWithoutImage
                          : styles.emailInputField,
                      )}
                      type="email"
                      placeholder="Email Address"
                      variant="underlined"
                      {...register('email')}
                      errorMessage={errors?.email?.message}
                      error={!!errors?.email}
                    />
                    <Button
                      className={clsx(
                        styles.btn,
                        !image && styles.btnWithoutImage,
                      )}
                      type="submit"
                    >
                      {!isLoading ? (
                        'Join'
                      ) : (
                        <PrimaryLoader
                          mode="segment"
                          size={18}
                          theme="light"
                          borderWidth={3}
                        />
                      )}
                    </Button>
                  </form>
                  {error && (
                    <InlineMessage
                      type="error"
                      message={
                        error ?? 'An error has occurred. Try later please.'
                      }
                    />
                  )}
                </div>
                <div
                  className={clsx(
                    styles.linkWrapper,
                    !image && styles.linkWrapperWithoutImage,
                  )}
                >
                  <Button
                    className={styles.linkClose}
                    as="text"
                    onClick={() => {
                      onCloseModal();
                    }}
                  >
                    No, Thanks. Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ModalSection>
      </Modal>
    )
  );
};

NewsLetterPopup.propTypes = {
  startModalWidth: PropTypes.number,
  label: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
  typograghyPosition: PropTypes.oneOf(['start', 'center', 'end']),
  image: PropTypes.any,
  onSet: PropTypes.func,
};

NewsLetterPopup.defaultProps = {
  startModalWidth: 750,
  label: '',
  title: '',
  text: '',
  typograghyPosition: 'start',
  image: null,
  onSet: () => {},
};

export default NewsLetterPopup;
